import type { DataFileName } from '../../../hooks/useWebsiteDataFile';

export interface CanonicalFieldRef {
  editableKey: string;
  file: DataFileName;
  path: string;
  label: string;
  direct: boolean;
  multiline?: boolean;
  maxLength?: number;
  aliases?: readonly string[];
}

export interface EditableFieldMatch extends CanonicalFieldRef {
  matchedKey: string;
}

interface DynamicFieldPattern {
  match: RegExp;
  build: (match: RegExpMatchArray) => CanonicalFieldRef;
}

const FIELD_REGISTRY: readonly CanonicalFieldRef[] = [
  {
    editableKey: 'business.name',
    file: 'business',
    path: 'name',
    label: 'Business name',
    direct: true,
    maxLength: 120,
    aliases: ['name'],
  },
  {
    editableKey: 'business.phone',
    file: 'business',
    path: 'phone',
    label: 'Phone number',
    direct: true,
    maxLength: 40,
    aliases: ['phone'],
  },
  {
    editableKey: 'hero.headline',
    file: 'content',
    path: 'hero.headline',
    label: 'Hero headline',
    direct: true,
    maxLength: 160,
    aliases: ['content.hero.headline', 'top-banner.headline'],
  },
  {
    editableKey: 'hero.headlineAccent',
    file: 'content',
    path: 'hero.headlineAccent',
    label: 'Hero highlighted text',
    direct: true,
    maxLength: 80,
    aliases: ['content.hero.headlineAccent', 'top-banner.headlineAccent'],
  },
  {
    editableKey: 'hero.description',
    file: 'content',
    path: 'hero.description',
    label: 'Hero description',
    direct: true,
    multiline: true,
    maxLength: 500,
    aliases: ['content.hero.description', 'top-banner.description'],
  },
  {
    editableKey: 'hero.image',
    file: 'content',
    path: 'hero.heroImage',
    label: 'Banner photo',
    direct: false,
    aliases: ['content.hero.heroImage', 'hero.heroImage'],
  },
  {
    editableKey: 'about.body',
    file: 'content',
    path: 'aboutSection.description',
    label: 'About description',
    direct: true,
    multiline: true,
    maxLength: 1200,
    aliases: ['about.description', 'content.aboutSection.description', 'aboutSection.description'],
  },
  {
    editableKey: 'cta.headline',
    file: 'content',
    path: 'ctaBanner.headline',
    label: 'Call-to-action headline',
    direct: true,
    maxLength: 180,
    aliases: ['content.ctaBanner.headline', 'ctaBanner.headline'],
  },
  {
    editableKey: 'cta.description',
    file: 'content',
    path: 'ctaBanner.description',
    label: 'Call-to-action description',
    direct: true,
    multiline: true,
    maxLength: 500,
  },
  {
    editableKey: 'cta.buttonText',
    file: 'content',
    path: 'ctaBanner.buttonText',
    label: 'Button label',
    direct: true,
    maxLength: 80,
  },
  {
    editableKey: 'cta.buttonHref',
    file: 'content',
    path: 'ctaBanner.buttonHref',
    label: 'Button destination',
    direct: false,
    maxLength: 2048,
  },
  {
    editableKey: 'pages.services.headline',
    file: 'content',
    path: 'pages.services.headline',
    label: 'Services page headline',
    direct: true,
    maxLength: 180,
    aliases: ['content.pages.services.headline'],
  },
  {
    editableKey: 'posts.0.title',
    file: 'posts',
    path: 'posts[0].title',
    label: 'Post title',
    direct: false,
    aliases: ['posts[0].title'],
  },
];

function normalizePathSegment(rawKey: string): string {
  return rawKey
    .trim()
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/\.+/g, '.')
    .replace(/^\./, '')
    .replace(/\.$/, '');
}

function aliasesFor(definition: CanonicalFieldRef): string[] {
  const filePrefixedPath = `${definition.file}.${normalizePathSegment(definition.path)}`;

  return [
    definition.editableKey,
    definition.path,
    filePrefixedPath,
    ...(definition.aliases ?? []),
  ].map(normalizePathSegment);
}

const FIELD_LOOKUP = new Map<string, CanonicalFieldRef>();

const DYNAMIC_FIELD_PATTERNS: readonly DynamicFieldPattern[] = [
  {
    match: /^services\.(\d+)\.(name|shortDescription|image)$/,
    build: ([, index, field]) => ({
      editableKey: `services.${index}.${field}`,
      file: 'services',
      path: `services[${index}].${field}`,
      label: field === 'name' ? 'Service name' : field === 'image' ? 'Service image' : 'Service short description',
      // Images go through ImageUpload in the panel; text is editable in place.
      direct: field !== 'image',
      multiline: field === 'shortDescription',
      maxLength: field === 'name' ? 80 : 300,
    }),
  },
  {
    match: /^posts\.(\d+)\.(title|excerpt|image)$/,
    build: ([, index, field]) => ({
      editableKey: `posts.${index}.${field}`,
      file: 'posts',
      path: `posts[${index}].${field}`,
      label: field === 'title' ? 'Post title' : field === 'image' ? 'Post image' : 'Post excerpt',
      direct: field !== 'image',
      multiline: field === 'excerpt',
      maxLength: field === 'title' ? 120 : 400,
    }),
  },
  {
    match: /^testimonials\.(\d+)\.(quote|author|text|name)$/,
    build: ([, index, field]) => {
      const canonicalField = field === 'author' || field === 'name' ? 'author' : 'quote';
      const pathField = canonicalField === 'author' ? 'name' : 'text';

      return {
        editableKey: `testimonials.${index}.${canonicalField}`,
        file: 'testimonials',
        path: `testimonials[${index}].${pathField}`,
        label: canonicalField === 'author' ? 'Review author' : 'Review quote',
        direct: true,
        multiline: canonicalField === 'quote',
        maxLength: canonicalField === 'author' ? 80 : 600,
      };
    },
  },
  {
    match: /^faqs\.(\d+)\.(question|answer)$/,
    build: ([, index, field]) => ({
      editableKey: `faqs.${index}.${field}`,
      file: 'faqs',
      path: `faqs[${index}].${field}`,
      label: field === 'question' ? 'FAQ question' : 'FAQ answer',
      direct: true,
      multiline: field === 'answer',
      maxLength: field === 'question' ? 200 : 1_200,
    }),
  },
  {
    match: /^categories\.0\.faqs\.(\d+)\.(question|answer)$/,
    build: ([, index, field]) => ({
      editableKey: `faqs.${index}.${field}`,
      file: 'faqs',
      path: `categories[0].faqs[${index}].${field}`,
      label: field === 'question' ? 'FAQ question' : 'FAQ answer',
      direct: true,
      multiline: field === 'answer',
      maxLength: field === 'question' ? 200 : 1_200,
    }),
  },
];

for (const definition of FIELD_REGISTRY) {
  for (const alias of aliasesFor(definition)) {
    FIELD_LOOKUP.set(alias, definition);
  }
}

export function getEditableFieldByKey(editableKey: string): EditableFieldMatch | null {
  const matchedKey = normalizePathSegment(editableKey);
  const definition = FIELD_LOOKUP.get(matchedKey);

  if (definition) {
    return {
      ...definition,
      matchedKey,
    };
  }

  for (const pattern of DYNAMIC_FIELD_PATTERNS) {
    const match = matchedKey.match(pattern.match);

    if (!match) {
      continue;
    }

    return {
      ...pattern.build(match),
      matchedKey,
    };
  }

  return null;
}

function buildDynamicEditableKey(file: DataFileName, normalizedPath: string): string | null {
  if (file === 'services') {
    const match = normalizedPath.match(/^services\.(\d+)\.(name|shortDescription|image)$/);
    if (match) {
      return `services.${match[1]}.${match[2]}`;
    }
  }

  if (file === 'posts') {
    const match = normalizedPath.match(/^posts\.(\d+)\.(title|excerpt|image)$/);
    if (match) return `posts.${match[1]}.${match[2]}`;
  }

  if (file === 'testimonials') {
    const match = normalizedPath.match(/^testimonials\.(\d+)\.(text|name)$/);
    if (match) {
      const stableField = match[2] === 'name' ? 'author' : 'quote';
      return `testimonials.${match[1]}.${stableField}`;
    }
  }

  if (file === 'faqs') {
    const nestedMatch = normalizedPath.match(/^categories\.0\.faqs\.(\d+)\.(question|answer)$/);
    if (nestedMatch) {
      return `faqs.${nestedMatch[1]}.${nestedMatch[2]}`;
    }

    const flatMatch = normalizedPath.match(/^faqs\.(\d+)\.(question|answer)$/);
    if (flatMatch) {
      return `faqs.${flatMatch[1]}.${flatMatch[2]}`;
    }
  }

  return null;
}

export function getEditableKeyForField(file: DataFileName, path: string): string | null {
  const normalizedPath = normalizePathSegment(path);
  const dynamicMatch = buildDynamicEditableKey(file, normalizedPath);

  if (dynamicMatch) {
    return dynamicMatch;
  }

  for (const definition of FIELD_REGISTRY) {
    if (definition.file === file && normalizePathSegment(definition.path) === normalizedPath) {
      return definition.editableKey;
    }
  }

  return null;
}

export function listEditableFields(): readonly CanonicalFieldRef[] {
  return FIELD_REGISTRY;
}

export function isSafeButtonDestination(rawValue: string): boolean {
  const value = rawValue.trim();
  if (!value) return true;
  if (value.length > 2048 || value.startsWith('//')) return false;
  if (value.startsWith('/') || value.startsWith('#')) return true;
  try {
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}
